import { z } from 'zod';

import { addressSchema } from './address';
import { currencySchema } from './currency';
import {
  galleryImageEntitySchema,
  galleryImageWireSchema,
  imageRefEntitySchema,
  imageRefWireSchema,
} from './image';
import { payoutScheduleSchema } from './payoutSchedule';
import {
  emailSchema,
  isoDateTimeSchema,
  minorUnitsBigintSchema,
  phoneSchema,
  slugSchema,
  ulidSchema,
} from './primitives';

export const restaurantStatusSchema = z.enum([
  'DRAFT',
  'PENDING_REVIEW',
  'APPROVED',
  'REJECTED',
  'PAUSED',
  'SUSPENDED',
]);
export type RestaurantStatus = z.infer<typeof restaurantStatusSchema>;

export const fulfillmentTypeSchema = z.enum(['PICKUP', 'RESTAURANT_DELIVERY', 'PLATFORM_DELIVERY']);
export type FulfillmentType = z.infer<typeof fulfillmentTypeSchema>;

/**
 * Cuisine taxonomy aimed at the Curaçao market. Values are stable English keys for the API;
 * UI layers handle Papiamentu/Dutch/English/Spanish translations. Keep this an enum so a future
 * `STATUS#APPROVED#CUISINE#<value>` GSI partition can be added without a data migration.
 */
export const cuisineSchema = z.enum([
  'krioyo',
  'caribbean',
  'dutch',
  'venezuelan',
  'colombian',
  'surinamese',
  'indonesian',
  'chinese',
  'italian',
  'american',
  'seafood',
  'bakery',
  'vegan',
  'other',
]);
export type Cuisine = z.infer<typeof cuisineSchema>;

/**
 * Service-fee percent stored as integer basis points (1/100 of a percent), e.g. 1500 = 15.00%.
 * Bigint to keep money math integer-only.
 */
export const serviceFeeBasisPointsSchema = z.bigint().nonnegative();
export type ServiceFeeBasisPoints = bigint;

/**
 * Per-language overrides for customer-visible restaurant text. Keys are BCP-47 language codes.
 * English is always the primary/fallback stored in the top-level `name` field.
 */
export const restaurantTranslationSchema = z.object({
  name: z.string().min(1).max(140).optional(),
});
export type RestaurantTranslation = z.infer<typeof restaurantTranslationSchema>;

export const restaurantTranslationsSchema = z.record(z.string(), restaurantTranslationSchema);
export type RestaurantTranslations = z.infer<typeof restaurantTranslationsSchema>;

/** Full DB record. Internal only. Never returned over the wire. */
export const restaurantEntitySchema = z.object({
  restaurantId: ulidSchema,
  slug: slugSchema,
  name: z.string().min(1).max(140),
  currency: currencySchema,
  address: addressSchema,
  contactEmail: emailSchema,
  contactPhone: phoneSchema,
  kvkNumber: z.string().max(40).optional(),

  offersRestaurantDelivery: z.boolean(),
  restaurantDeliveryRadiusKilometers: z.number().nonnegative().optional(),
  acceptsPickup: z.boolean(),
  allowsPlatformDelivery: z.boolean().default(false),

  cuisines: z.array(cuisineSchema).max(5).default([]),
  /**
   * Denormalized lowercased token set built from `name + cuisines + menu item names`. Maintained
   * atomically with the source fields (`META.infoVersion` / `META.menuVersion` bumps). Internal
   * only: never projected to wire schemas. Powers substring/cuisine filtering on the discovery
   * GSI2 query.
   */
  searchTokens: z.array(z.string().min(1)).default([]),

  status: restaurantStatusSchema,
  acceptingOrders: z.boolean(),
  rejectionReasonNote: z.string().optional(),
  submittedForReviewAt: isoDateTimeSchema.optional(),
  approvedAt: isoDateTimeSchema.optional(),
  lastStatusChangedAt: isoDateTimeSchema,

  confirmTimeoutMinutes: z.number().int().min(1).max(180).default(30),

  payoutSchedule: payoutScheduleSchema,
  payoutMinimumMinorUnits: minorUnitsBigintSchema,
  payoutServiceFeeMinorUnits: minorUnitsBigintSchema,
  serviceFeeBasisPoints: serviceFeeBasisPointsSchema,
  paymentProviderId: z.string().min(1),
  brandId: ulidSchema.optional(),

  logo: imageRefEntitySchema.optional(),
  coverImage: imageRefEntitySchema.optional(),
  gallery: z.array(galleryImageEntitySchema).default([]),

  reviewCount: z.number().int().nonnegative().default(0),
  reviewSumStars: z.number().int().nonnegative().default(0),

  /**
   * Edge-cache version counters. Bumped atomically with every mutation that affects either
   * the public detail snapshot (`infoVersion`) or the public menu snapshot (`menuVersion`).
   * See `bonboca-data.mdc` for the bump rules. Defaults to `1` so newly-persisted records
   * already have a usable snapshot URL.
   */
  infoVersion: z.number().int().nonnegative().default(1),
  menuVersion: z.number().int().nonnegative().default(1),

  translations: restaurantTranslationsSchema.optional(),

  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type RestaurantEntity = z.infer<typeof restaurantEntitySchema>;

/** Public projection — anonymous customers / discovery. */
export const restaurantPublicSchema = z.object({
  restaurantId: ulidSchema,
  slug: slugSchema,
  name: z.string(),
  address: addressSchema,
  currency: currencySchema,
  contactPhone: phoneSchema,
  cuisines: z.array(cuisineSchema).default([]),
  offersRestaurantDelivery: z.boolean(),
  restaurantDeliveryRadiusKilometers: z.number().nonnegative().optional(),
  acceptsPickup: z.boolean(),
  availableFulfillmentTypes: z.array(fulfillmentTypeSchema),
  acceptingOrders: z.boolean(),
  distanceKilometers: z.number().nonnegative().nullable().optional(),
  logo: imageRefWireSchema.optional(),
  coverImage: imageRefWireSchema.optional(),
  gallery: z.array(galleryImageWireSchema).default([]),
  reviewCount: z.number().int().nonnegative(),
  reviewAverageStars: z.number().min(1).max(5).nullable(),
  translations: restaurantTranslationsSchema.optional(),
});
export type RestaurantPublic = z.infer<typeof restaurantPublicSchema>;

/** Owner projection — restaurant members. */
export const restaurantOwnerSchema = restaurantPublicSchema.extend({
  contactEmail: emailSchema,
  status: restaurantStatusSchema,
  rejectionReasonNote: z.string().optional(),
  submittedForReviewAt: isoDateTimeSchema.optional(),
  approvedAt: isoDateTimeSchema.optional(),
  lastStatusChangedAt: isoDateTimeSchema,
  payoutSchedule: payoutScheduleSchema,
  payoutMinimumMinorUnits: z.string().describe('bigint serialized as string'),
  serviceFeeBasisPoints: z.string().describe('bigint serialized as string'),
  payoutServiceFeeMinorUnits: z.string().describe('bigint serialized as string'),
  paymentProviderId: z.string(),
  confirmTimeoutMinutes: z.number().int(),
});
export type RestaurantOwner = z.infer<typeof restaurantOwnerSchema>;

/** Admin projection — adds compliance + reserved fields. */
export const restaurantAdminSchema = restaurantOwnerSchema.extend({
  kvkNumber: z.string().optional(),
  brandId: ulidSchema.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type RestaurantAdmin = z.infer<typeof restaurantAdminSchema>;
